/**
 * Enhanced Search Overlay - Klevu Style
 * Shopify Predictive Search with images, prices, categories, recent searches
 */

class EnhancedSearchOverlay {
  constructor() {
    this.overlay = document.querySelector('[data-search-overlay]');
    this.input = document.querySelector('[data-search-input]');
    this.backdrop = document.querySelector('[data-search-backdrop]');
    this.clearBtn = document.querySelector('[data-search-clear]');
    this.submitBtn = document.querySelector('[data-search-submit]');
    this.resultsContainer = document.querySelector('[data-search-results]');
    this.loadingState = document.querySelector('[data-search-loading]');
    this.initialState = document.querySelector('[data-search-initial]');
    this.suggestionsState = document.querySelector('[data-search-suggestions]');
    this.noResultsState = document.querySelector('[data-no-results]');
    this.viewAllSection = document.querySelector('[data-mh-view-all]');
    this.productsList = document.querySelector('[data-products-list]');
    this.categoriesSection = document.querySelector('[data-categories-section]');
    this.categoriesList = document.querySelector('[data-categories-list]');
    this.recentSearchesList = document.querySelector('[data-recent-list]');
    this.recentSearchesSection = document.querySelector('[data-recent-searches]');
    this.trendingSearchesSection = document.querySelector('[data-trending-searches]');
    this.trendingTagsContainer = document.querySelector('[data-trending-tags]');

    this.searchTimeout = null;
    this.currentQuery = '';
    this.recentSearches = this.loadRecentSearches();
    this.trendingTerms = null; // null = not yet fetched
    this.isOpen = false;
    this.touchStartY = 0;
    this.moneyFormat = window.__mhMoneyFormat || '{{amount}} QAR';

    // Desktop trending dropdown elements
    this.nativeSearchInput = document.querySelector('.search-input-group .search-input, input.search-input[name="q"]');
    this.nativeSearchForm = this.nativeSearchInput ? this.nativeSearchInput.closest('.enhanced-search-dropdown-wrapper, .search-form') : null;
    this.trendingDropdown = null; // created dynamically
    this.trendingDropdownOpen = false;

    // Popular products scraped from Liquid-rendered DOM (for desktop dropdown reuse)
    this.popularProducts = this.scrapePopularProducts();

    if (this.overlay) this.init();
  }

  init() {
    this.bindEvents();
    this.renderRecentSearches();
    this.setupMobileGestures();
    this.createTrendingDropdown();
    // Pre-fetch trending data so it's instant when user clicks
    this.fetchTrendingSearches();
  }

  /**
   * Extract popular products from the Liquid-rendered overlay DOM
   * so we can reuse them in the desktop trending dropdown.
   */
  scrapePopularProducts() {
    var products = [];
    var cards = document.querySelectorAll('[data-popular-products] .popular-product-card');
    cards.forEach(function(card) {
      var img = card.querySelector('img');
      var titleEl = card.querySelector('.popular-product-card__title');
      var priceEl = card.querySelector('.popular-product-card__price');
      products.push({
        url: card.getAttribute('href') || '#',
        image: img ? img.getAttribute('src') : '',
        title: titleEl ? titleEl.textContent.trim() : '',
        price: priceEl ? priceEl.textContent.trim() : ''
      });
    });
    return products;
  }

  // ── Desktop Trending Dropdown (shown on focus, before typing) ──
  createTrendingDropdown() {
    if (!this.nativeSearchInput) return;

    var wrapper = this.nativeSearchInput.closest('.enhanced-search-dropdown-wrapper') || this.nativeSearchInput.closest('.search-form');
    if (!wrapper) return;

    // Ensure wrapper is positioned for absolute children
    if (getComputedStyle(wrapper).position === 'static') {
      wrapper.style.position = 'relative';
    }

    var dropdown = document.createElement('div');
    dropdown.className = 'mh-trending-dropdown';
    dropdown.style.display = 'none';
    wrapper.appendChild(dropdown);
    this.trendingDropdown = dropdown;
  }

  showTrendingDropdown() {
    // Disabled on desktop — unified dropdown in header_5.liquid handles focus-state
    if (window.innerWidth > 768) return;
    if (!this.trendingDropdown || this.isOpen) return;
    if (this.trendingDropdownOpen) return;

    // Don't show on mobile — use the full overlay instead
    if (window.innerWidth <= 768) {
      this.open();
      return;
    }

    this.trendingDropdownOpen = true;
    this.trendingDropdown.style.display = 'block';

    // If data already cached, render immediately; otherwise fetch
    if (this.trendingTerms !== null) {
      this.renderTrendingDropdown();
    } else {
      this.fetchTrendingSearches();
    }
  }

  hideTrendingDropdown() {
    if (!this.trendingDropdown) return;
    this.trendingDropdownOpen = false;
    this.trendingDropdown.style.display = 'none';
  }

  renderTrendingDropdown() {
    if (!this.trendingDropdown) return;
    if (!this.trendingTerms || this.trendingTerms.length === 0) {
      this.trendingDropdown.style.display = 'none';
      return;
    }

    var self = this;
    var trendSvg = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>';

    this.trendingDropdown.textContent = '';

    // ── Top: Trending search tags (full width) ──
    var header = document.createElement('div');
    header.className = 'mh-trending-dropdown__header';
    header.textContent = 'Trending searches';
    this.trendingDropdown.appendChild(header);

    var tagsWrap = document.createElement('div');
    tagsWrap.className = 'mh-trending-dropdown__tags';

    this.trendingTerms.forEach(function(term) {
      var btn = document.createElement('button');
      btn.className = 'trending-tag';

      var iconSpan = document.createElement('span');
      iconSpan.className = 'trending-tag__icon';
      iconSpan.innerHTML = trendSvg;

      var textSpan = document.createElement('span');
      textSpan.textContent = term;

      btn.appendChild(iconSpan);
      btn.appendChild(textSpan);

      btn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        if (self.nativeSearchInput) {
          self.nativeSearchInput.value = term;
        }
        self.searchInDropdown(term);
      });

      tagsWrap.appendChild(btn);
    });

    this.trendingDropdown.appendChild(tagsWrap);

    // ── Bottom: Two-column layout (Recent searches LEFT | Popular products RIGHT) ──
    // If no recent searches → full-width popular products grid
    var hasRecent = self.recentSearches.length > 0;
    var hasProducts = self.popularProducts && self.popularProducts.length > 0;

    if (hasRecent && hasProducts) {
      // Two-column layout
      var columnsWrap = document.createElement('div');
      columnsWrap.className = 'mh-trending-bottom-columns';

      // LEFT: Recent searches
      var leftCol = document.createElement('div');
      leftCol.className = 'mh-trending-bottom-left';

      var recentHeader = document.createElement('div');
      recentHeader.className = 'mh-trending-dropdown__header';
      recentHeader.textContent = 'Recent searches';
      leftCol.appendChild(recentHeader);

      self.recentSearches.forEach(function(query) {
        var item = document.createElement('div');
        item.className = 'mh-trending-dropdown__recent-item';

        var clockSvg = document.createElement('span');
        clockSvg.innerHTML = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 1v6l4 2" stroke="#9CA3AF" stroke-width="1.5" stroke-linecap="round"/><circle cx="8" cy="8" r="7" stroke="#9CA3AF" stroke-width="1.5"/></svg>';

        var text = document.createElement('span');
        text.textContent = query;

        item.appendChild(clockSvg);
        item.appendChild(text);

        item.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          if (self.nativeSearchInput) {
            self.nativeSearchInput.value = query;
          }
          self.searchInDropdown(query);
        });

        leftCol.appendChild(item);
      });

      columnsWrap.appendChild(leftCol);

      // RIGHT: Popular products
      var rightCol = document.createElement('div');
      rightCol.className = 'mh-trending-bottom-right';

      var prodHeader = document.createElement('div');
      prodHeader.className = 'mh-trending-dropdown__header';
      prodHeader.textContent = 'Popular Products';
      rightCol.appendChild(prodHeader);

      var prodGrid = document.createElement('div');
      prodGrid.className = 'mh-trending-popular-grid';

      self.popularProducts.forEach(function(p) {
        var card = document.createElement('a');
        card.href = p.url;
        card.className = 'mh-trending-popular-card';

        if (p.image) {
          var img = document.createElement('img');
          img.src = p.image;
          img.alt = p.title;
          img.loading = 'lazy';
          card.appendChild(img);
        }

        var title = document.createElement('div');
        title.className = 'mh-trending-popular-card__title';
        title.textContent = p.title;
        card.appendChild(title);

        var price = document.createElement('div');
        price.className = 'mh-trending-popular-card__price';
        price.textContent = p.price;
        card.appendChild(price);

        prodGrid.appendChild(card);
      });

      rightCol.appendChild(prodGrid);
      columnsWrap.appendChild(rightCol);
      this.trendingDropdown.appendChild(columnsWrap);

    } else if (hasRecent) {
      // Only recent searches — no products
      var recentHeader = document.createElement('div');
      recentHeader.className = 'mh-trending-dropdown__header';
      recentHeader.textContent = 'Recent searches';
      self.trendingDropdown.appendChild(recentHeader);

      self.recentSearches.forEach(function(query) {
        var item = document.createElement('div');
        item.className = 'mh-trending-dropdown__recent-item';

        var clockSvg = document.createElement('span');
        clockSvg.innerHTML = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 1v6l4 2" stroke="#9CA3AF" stroke-width="1.5" stroke-linecap="round"/><circle cx="8" cy="8" r="7" stroke="#9CA3AF" stroke-width="1.5"/></svg>';

        var text = document.createElement('span');
        text.textContent = query;

        item.appendChild(clockSvg);
        item.appendChild(text);

        item.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          if (self.nativeSearchInput) {
            self.nativeSearchInput.value = query;
          }
          self.searchInDropdown(query);
        });

        self.trendingDropdown.appendChild(item);
      });

    } else if (hasProducts) {
      // Only popular products — full-width grid
      var prodHeader = document.createElement('div');
      prodHeader.className = 'mh-trending-dropdown__header';
      prodHeader.textContent = 'Popular Products';
      self.trendingDropdown.appendChild(prodHeader);

      var prodGrid = document.createElement('div');
      prodGrid.className = 'mh-trending-popular-grid mh-trending-popular-grid--full';

      self.popularProducts.forEach(function(p) {
        var card = document.createElement('a');
        card.href = p.url;
        card.className = 'mh-trending-popular-card';

        if (p.image) {
          var img = document.createElement('img');
          img.src = p.image;
          img.alt = p.title;
          img.loading = 'lazy';
          card.appendChild(img);
        }

        var title = document.createElement('div');
        title.className = 'mh-trending-popular-card__title';
        title.textContent = p.title;
        card.appendChild(title);

        var price = document.createElement('div');
        price.className = 'mh-trending-popular-card__price';
        price.textContent = p.price;
        card.appendChild(price);

        prodGrid.appendChild(card);
      });

      self.trendingDropdown.appendChild(prodGrid);
    }
  }

  // Search and show results inside the dropdown (not the overlay)
  searchInDropdown(query) {
    if (!this.trendingDropdown) return;

    this.saveRecentSearch(query);
    this._logSearchToBackend(query, 'submit');

    // Show loading in dropdown
    this.trendingDropdown.textContent = '';
    var loadingDiv = document.createElement('div');
    loadingDiv.style.cssText = 'text-align:center;padding:30px;color:#9CA3AF;font-size:14px;';
    loadingDiv.textContent = 'Searching...';
    this.trendingDropdown.appendChild(loadingDiv);

    var self = this;
    // MenaHub brand+category+spec-aware engine (same as the /search page), not
    // Shopify's native /search/suggest.json which only token-matches and mixed
    // brands/categories in the dropdown.
    var url = 'https://admin.menahub.com/api/search.php?q=' + encodeURIComponent(query) + '&limit=10';

    fetch(url, { credentials: 'omit' })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        var items = (data && data.items) || [];
        var products = items.map(function(it) {
          return {
            title: it.title || '',
            url: it.shopify_handle ? '/products/' + it.shopify_handle : ('/search?q=' + encodeURIComponent(query)),
            image: it.image || (it.images && it.images[0]) || '',
            price: it.price
          };
        });
        self.renderDropdownResults(products, [], query);
      })
      .catch(function() {
        self.trendingDropdown.textContent = '';
        var errDiv = document.createElement('div');
        errDiv.style.cssText = 'text-align:center;padding:30px;color:#DC2626;font-size:14px;';
        errDiv.textContent = 'Something went wrong. Please try again.';
        self.trendingDropdown.appendChild(errDiv);
      });
  }

  renderDropdownResults(products, collections, query) {
    if (!this.trendingDropdown) return;
    this.trendingDropdown.textContent = '';

    var self = this;

    // No results
    if (products.length === 0 && collections.length === 0) {
      var noRes = document.createElement('div');
      noRes.style.cssText = 'text-align:center;padding:30px;color:#6B7280;font-size:14px;';
      noRes.textContent = 'No results found for "' + query + '"';
      this.trendingDropdown.appendChild(noRes);

      // Back to trending
      var backBtn = document.createElement('div');
      backBtn.className = 'mh-dropdown-back';
      var backLink = document.createElement('button');
      backLink.className = 'mh-dropdown-back__btn';
      backLink.textContent = '← Back to trending';
      backLink.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        if (self.nativeSearchInput) self.nativeSearchInput.value = '';
        self.renderTrendingDropdown();
      });
      backBtn.appendChild(backLink);
      this.trendingDropdown.appendChild(backBtn);
      return;
    }

    // ── Two-column layout: keywords left, products right ──
    var columnsWrap = document.createElement('div');
    columnsWrap.className = 'mh-dropdown-columns';

    // ── LEFT COLUMN: Search suggestions / categories ──
    var leftCol = document.createElement('div');
    leftCol.className = 'mh-dropdown-col-left';

    // Search query as suggestion link
    var suggestHeader = document.createElement('div');
    suggestHeader.className = 'mh-dropdown-col-header';
    suggestHeader.textContent = 'Suggestions';
    leftCol.appendChild(suggestHeader);

    // Main search term
    var mainSuggest = document.createElement('a');
    mainSuggest.href = '/search?q=' + encodeURIComponent(query) + '&type=product';
    mainSuggest.className = 'mh-dropdown-suggest-item mh-dropdown-suggest-item--active';
    var searchIcon = document.createElement('span');
    searchIcon.className = 'mh-dropdown-suggest-icon';
    searchIcon.innerHTML = '<svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM19 19l-4.35-4.35" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
    var searchText = document.createElement('span');
    searchText.textContent = query;
    mainSuggest.appendChild(searchIcon);
    mainSuggest.appendChild(searchText);
    leftCol.appendChild(mainSuggest);

    // Collection/category suggestions
    if (collections.length > 0) {
      var catHeader = document.createElement('div');
      catHeader.className = 'mh-dropdown-col-header';
      catHeader.style.marginTop = '14px';
      catHeader.textContent = 'Categories';
      leftCol.appendChild(catHeader);

      collections.forEach(function(col) {
        var catLink = document.createElement('a');
        catLink.href = col.url;
        catLink.className = 'mh-dropdown-suggest-item';
        var folderIcon = document.createElement('span');
        folderIcon.className = 'mh-dropdown-suggest-icon';
        folderIcon.innerHTML = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 2h5l1 1H14v10H2V2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        var catText = document.createElement('span');
        catText.textContent = col.title;
        catLink.appendChild(folderIcon);
        catLink.appendChild(catText);
        leftCol.appendChild(catLink);
      });
    }

    // Back to trending link at bottom of left column
    var backLink = document.createElement('button');
    backLink.className = 'mh-dropdown-back__btn';
    backLink.style.marginTop = '14px';
    backLink.textContent = '← Trending';
    backLink.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      if (self.nativeSearchInput) self.nativeSearchInput.value = '';
      self.renderTrendingDropdown();
    });
    leftCol.appendChild(backLink);

    columnsWrap.appendChild(leftCol);

    // ── RIGHT COLUMN: Product results ──
    var rightCol = document.createElement('div');
    rightCol.className = 'mh-dropdown-col-right';

    if (products.length > 0) {
      var prodHeader = document.createElement('div');
      prodHeader.className = 'mh-dropdown-col-header';
      prodHeader.textContent = 'Products';
      var countSpan = document.createElement('span');
      countSpan.style.cssText = 'float:right;color:#9CA3AF;font-weight:400;font-size:12px;';
      countSpan.textContent = products.length + ' found';
      prodHeader.appendChild(countSpan);
      rightCol.appendChild(prodHeader);

      var prodList = document.createElement('div');
      prodList.className = 'mh-dropdown-products';

      products.forEach(function(product) {
        var link = document.createElement('a');
        link.href = product.url;
        link.className = 'mh-dropdown-product-item';

        // Image
        var imgWrap = document.createElement('div');
        imgWrap.className = 'mh-dropdown-product-item__img';
        var imageUrl = product.image || product.featured_image || '';
        if (imageUrl) {
          if (imageUrl.startsWith('//')) imageUrl = 'https:' + imageUrl;
          var img = document.createElement('img');
          img.src = imageUrl;
          img.alt = product.title;
          img.loading = 'lazy';
          imgWrap.appendChild(img);
        }
        link.appendChild(imgWrap);

        // Details
        var details = document.createElement('div');
        details.className = 'mh-dropdown-product-item__info';

        var title = document.createElement('div');
        title.className = 'mh-dropdown-product-item__title';
        title.textContent = product.title;
        details.appendChild(title);

        var price = document.createElement('div');
        price.className = 'mh-dropdown-product-item__price';
        price.textContent = self.formatMoney(product.price);
        details.appendChild(price);

        link.appendChild(details);
        prodList.appendChild(link);
      });

      rightCol.appendChild(prodList);
    }

    columnsWrap.appendChild(rightCol);
    this.trendingDropdown.appendChild(columnsWrap);

    // ── Fixed "View all results" at the bottom (full width) ──
    if (products.length > 0) {
      var viewAllWrap = document.createElement('div');
      viewAllWrap.className = 'mh-dropdown-view-all';
      var viewAllLink = document.createElement('a');
      viewAllLink.href = '/search?q=' + encodeURIComponent(query) + '&type=product';
      viewAllLink.className = 'mh-dropdown-view-all__link';
      viewAllLink.textContent = 'View all results';
      var arrow = document.createElement('span');
      arrow.innerHTML = ' ›';
      viewAllLink.appendChild(arrow);

      viewAllLink.addEventListener('click', function() {
        self.saveRecentSearch(query);
      });

      viewAllWrap.appendChild(viewAllLink);
      this.trendingDropdown.appendChild(viewAllWrap);
    }
  }

  bindEvents() {
    var self = this;

    // Open from [data-search-trigger] elements
    document.addEventListener('click', function(e) {
      var trigger = e.target.closest('[data-search-trigger]');
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        self.open();
      }
    });

    // Open from mobile header search icon (existing theme markup)
    document.addEventListener('click', function(e) {
      var mobileSearch = e.target.closest('.mobile-header-search .search-active, .mobile-header-search a, .header-search a');
      if (mobileSearch) {
        e.preventDefault();
        e.stopPropagation();
        self.open();
        return false;
      }
    }, true);

    // Open from desktop header search icon
    document.addEventListener('click', function(e) {
      var desktopSearch = e.target.closest('.header-search .search-active');
      if (desktopSearch) {
        e.preventDefault();
        e.stopPropagation();
        self.open();
        return false;
      }
    }, true);

    // Desktop: click/focus on native search input → show trending dropdown (not overlay)
    if (this.nativeSearchInput) {
      // Use capturing mousedown — fires before any theme handler can intercept
      // Call showTrendingDropdown() directly (NOT inside requestAnimationFrame)
      // because rAF defers to next paint frame, by which time theme JS may have
      // already consumed the event. Direct call in capture phase runs first.
      this.nativeSearchInput.addEventListener('mousedown', function() {
        if (!self.isOpen) {
          self.showTrendingDropdown();
        }
      }, true);

      this.nativeSearchInput.addEventListener('focus', function() {
        if (!self.isOpen && !self.trendingDropdownOpen) {
          self.showTrendingDropdown();
        }
      });

      // When user starts typing in native input → search in dropdown
      this.nativeSearchInput.addEventListener('input', function() {
        var val = self.nativeSearchInput.value.trim();
        if (val.length === 0) {
          // Show trending again
          self.trendingDropdownOpen = true;
          self.trendingDropdown.style.display = 'block';
          self.renderTrendingDropdown();
          return;
        }
        if (val.length >= 2) {
          clearTimeout(self._nativeSearchTimeout);
          self._nativeSearchTimeout = setTimeout(function() {
            self.searchInDropdown(val);
          }, 300);
        }
      });

      // Native form submit → go to search results page
      var nativeForm = this.nativeSearchInput.closest('form');
      if (nativeForm) {
        nativeForm.addEventListener('submit', function(e) {
          e.preventDefault();
          var val = self.nativeSearchInput.value.trim();
          if (val.length > 0) {
            self.hideTrendingDropdown();
            self.saveRecentSearch(val);
            self._logSearchToBackend(val, 'submit');
            window.location.href = '/search?q=' + encodeURIComponent(val) + '&type=product';
          }
        });
      }

      // Keydown on native input — Enter to submit
      this.nativeSearchInput.addEventListener('keydown', function(e) {
        if (e.key === 'Escape') {
          self.hideTrendingDropdown();
          self.nativeSearchInput.blur();
        }
      });
    }

    // Also open trending dropdown when clicking the search container area (icon, padding, etc.)
    var searchContainer = document.querySelector('.header-search-container, .enhanced-search-dropdown-wrapper');
    if (searchContainer) {
      searchContainer.addEventListener('click', function(e) {
        // Don't interfere if clicking inside the dropdown itself or the Search button
        if (e.target.closest('.mh-trending-dropdown') || e.target.closest('.search-submit-btn')) return;
        if (!self.isOpen && !self.trendingDropdownOpen) {
          self.showTrendingDropdown();
          if (self.nativeSearchInput) self.nativeSearchInput.focus();
        }
      });
    }

    // Close trending dropdown when clicking outside
    // Use mousedown instead of click — mousedown fires before focus,
    // so we check if the target is outside before focus opens the dropdown
    document.addEventListener('mousedown', function(e) {
      if (self.trendingDropdownOpen && !e.target.closest('.mh-trending-dropdown') && !e.target.closest('.search-input-group') && !e.target.closest('.header-search-container') && !e.target.closest('.enhanced-search-dropdown-wrapper')) {
        self.hideTrendingDropdown();
      }
    });

    // Custom event listener
    document.addEventListener('openSearch', function() {
      self.open();
    });

    // Close events - all [data-search-close] buttons
    document.querySelectorAll('[data-search-close]').forEach(function(btn) {
      btn.addEventListener('click', function() { self.close(); });
    });

    if (this.backdrop) {
      this.backdrop.addEventListener('click', function() { self.close(); });
    }

    // Input events
    if (this.input) {
      this.input.addEventListener('input', function(e) { self.handleInput(e); });
      this.input.addEventListener('keydown', function(e) { self.handleKeydown(e); });
    }

    // Clear button
    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', function() { self.clearSearch(); });
    }

    // Search submit button
    if (this.submitBtn) {
      this.submitBtn.addEventListener('click', function() { self.submitSearch(); });
    }

    // Recent search clicks
    if (this.recentSearchesList) {
      this.recentSearchesList.addEventListener('click', function(e) {
        var item = e.target.closest('.recent-search-item');
        if (item) {
          var query = item.getAttribute('data-query');
          self.input.value = query;
          self.clearBtn.classList.add('visible');
          self.performSearch(query);
        }
      });
    }

    // Clear recent searches
    var clearRecentBtn = document.querySelector('[data-clear-recent]');
    if (clearRecentBtn) {
      clearRecentBtn.addEventListener('click', function() {
        self.clearRecentSearches();
      });
    }

    // Save recent search when clicking a product result
    if (this.productsList) {
      this.productsList.addEventListener('click', function() {
        var query = self.input ? self.input.value.trim() : '';
        if (query.length >= 2) {
          self.saveRecentSearch(query);
        }
      });
    }

    // View All click — navigate to full search results page
    var viewAllLink = document.querySelector('[data-mh-view-all-link]');
    if (viewAllLink) {
      viewAllLink.addEventListener('click', function(e) {
        e.preventDefault();
        var query = self.input ? self.input.value.trim() : '';
        if (query.length > 0) {
          self.saveRecentSearch(query);
          self._logSearchToBackend(query, 'submit');
          window.location.href = '/search?q=' + encodeURIComponent(query) + '&type=product';
        }
      });
    }

    // ESC key to close
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && self.isOpen) {
        self.close();
      }
    });
  }

  open() {
    if (!this.overlay) return;

    this.hideTrendingDropdown();
    this.overlay.classList.add('active');
    document.body.style.overflow = 'hidden';
    this.isOpen = true;

    var self = this;
    setTimeout(function() {
      if (self.input) self.input.focus();
    }, 150);

    this.showInitialState();
    this.fetchTrendingSearches();
  }

  close() {
    if (!this.overlay) return;

    this.overlay.classList.remove('active');
    document.body.style.overflow = '';
    this.isOpen = false;
    this.clearSearch();
  }

  handleInput(e) {
    var query = e.target.value.trim();

    if (query.length > 0) {
      this.clearBtn && this.clearBtn.classList.add('visible');
    } else {
      this.clearBtn && this.clearBtn.classList.remove('visible');
      this.showInitialState();
      return;
    }

    clearTimeout(this.searchTimeout);

    if (query.length >= 2) {
      var self = this;
      this.searchTimeout = setTimeout(function() {
        self.performSearch(query);
      }, 300);
    }
  }

  handleKeydown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.submitSearch();
    }
  }

  submitSearch() {
    var query = this.input ? this.input.value.trim() : '';
    if (query.length > 0) {
      this.saveRecentSearch(query);
      this._logSearchToBackend(query, 'submit');
      window.location.href = '/search?q=' + encodeURIComponent(query) + '&type=product';
    }
  }

  clearSearch() {
    if (this.input) this.input.value = '';
    this.clearBtn && this.clearBtn.classList.remove('visible');
    this.currentQuery = '';
    this.showInitialState();
  }

  performSearch(query) {
    if (query === this.currentQuery) return;
    this.currentQuery = query;
    this._logSearchToBackend(query, 'typing');

    this.showLoadingState();

    var self = this;
    // MenaHub brand+category+spec-aware engine (same as the /search page), not
    // Shopify's native /search/suggest.json which only token-matched.
    var url = 'https://admin.menahub.com/api/search.php?q=' + encodeURIComponent(query) + '&limit=10';

    fetch(url, { credentials: 'omit' })
      .then(function(response) {
        if (!response.ok) throw new Error('Search API error');
        return response.json();
      })
      .then(function(data) {
        var items = (data && data.items) || [];
        var products = items.map(function(it) {
          return {
            title: it.title || '',
            url: it.shopify_handle ? '/products/' + it.shopify_handle : ('/search?q=' + encodeURIComponent(query)),
            image: it.image || (it.images && it.images[0]) || '',
            price: it.price,
            compare_at_price: it.old_price || ''
          };
        });

        if (products.length === 0) {
          self.showNoResults(query);
        } else {
          self.displayResults(products, [], query);
        }
      })
      .catch(function() {
        self.showError();
      });
  }

  displayResults(products, collections, query) {
    this.hideAllStates();
    this.suggestionsState.style.display = 'block';

    // Render category chips (horizontal scroll)
    if (collections.length > 0 && this.categoriesSection && this.categoriesList) {
      this.categoriesSection.style.display = 'block';
      this.categoriesList.innerHTML = collections.map(function(col) {
        return '<a href="' + col.url + '" class="search-category-chip">'
          + '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 2h5l1 1H14v10H2V2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
          + '<span>' + col.title + '</span>'
          + '</a>';
      }).join('');
    } else if (this.categoriesSection) {
      this.categoriesSection.style.display = 'none';
    }

    // Render product count
    var countEl = document.querySelector('[data-products-count]');
    if (countEl) {
      countEl.textContent = products.length + ' found';
    }

    // Render product cards
    var self = this;
    this.productsList.innerHTML = products.map(function(product) {
      return self.renderProductCard(product);
    }).join('');

    // View all button — always show when there are results
    if (products.length > 0) {
      this.viewAllSection.style.display = 'block';
      var viewAllLink = document.querySelector('[data-mh-view-all-link]');
      if (viewAllLink) {
        viewAllLink.href = '/search?q=' + encodeURIComponent(query) + '&type=product';
      }
    } else {
      this.viewAllSection.style.display = 'none';
    }

  }

  renderProductCard(product) {
    // Shopify suggest API uses "image" field, not "featured_image"
    var imageUrl = product.image || product.featured_image || '';
    var imageHtml;

    if (imageUrl) {
      // Ensure proper protocol
      if (imageUrl.startsWith('//')) imageUrl = 'https:' + imageUrl;
      imageHtml = '<img src="' + imageUrl + '" alt="' + this.escapeHtml(product.title) + '" loading="lazy">';
    } else {
      imageHtml = '<div style="width:100%;height:100%;background:#f3f4f6;display:flex;align-items:center;justify-content:center;color:#ccc;font-size:20px;">'
        + this.escapeHtml(product.title.charAt(0).toUpperCase()) + '</div>';
    }

    var priceHtml = '';
    var currentPrice = product.price;
    var comparePrice = product.compare_at_price_max || product.compare_at_price || '';

    if (currentPrice) {
      priceHtml = '<div class="search-product-item__price"><span class="price-current">' + this.formatMoney(currentPrice) + '</span>';

      if (comparePrice && parseFloat(comparePrice) > parseFloat(currentPrice)) {
        var discount = Math.round((1 - parseFloat(currentPrice) / parseFloat(comparePrice)) * 100);
        priceHtml += '<span class="price-compare">' + this.formatMoney(comparePrice) + '</span>';
        priceHtml += '<span class="price-badge">-' + discount + '%</span>';
      }

      priceHtml += '</div>';
    }

    return '<a href="' + product.url + '" class="search-product-item">'
      + '<div class="search-product-item__image">' + imageHtml + '</div>'
      + '<div class="search-product-item__details">'
      + '<h4 class="search-product-item__title">' + this.escapeHtml(product.title) + '</h4>'
      + priceHtml
      + '</div>'
      + '</a>';
  }

  showLoadingState() {
    this.hideAllStates();
    this.loadingState.style.display = 'block';
  }

  showInitialState() {
    this.hideAllStates();
    this.initialState.style.display = 'block';
    this.renderRecentSearches();
  }

  showNoResults(query) {
    this.hideAllStates();
    this.noResultsState.style.display = 'block';
    var termEl = this.noResultsState.querySelector('[data-search-term]');
    if (termEl) termEl.textContent = query;
  }

  showError() {
    this.hideAllStates();
    if (this.resultsContainer) {
      this.resultsContainer.innerHTML = '<div style="text-align:center;padding:40px;color:#DC2626;">An error occurred. Please try again.</div>';
    }
  }

  hideAllStates() {
    if (this.loadingState) this.loadingState.style.display = 'none';
    if (this.initialState) this.initialState.style.display = 'none';
    if (this.suggestionsState) this.suggestionsState.style.display = 'none';
    if (this.noResultsState) this.noResultsState.style.display = 'none';
    if (this.viewAllSection) this.viewAllSection.style.display = 'none';
  }

  // Recent Searches
  /**
   * Decide whether a query looks like junk / bot input / mistyping that
   * should never appear in the Recent searches list.
   *
   * Tunable rules — adjust thresholds to fit catalog terms. Real product
   * searches like "3M", "7UP", "WD40", "iPhone" must pass; spam like
   * "hhhhhhhhhhh", "aaaaa", "qwerqwerqwer", "...." must fail.
   *
   * Current rules:
   *   1. Cap absolute length (real product names rarely > 40 chars).
   *   2. Reject if ≥ 70% of characters are the same single character
   *      AND length ≥ 5 — catches "hhhhhh", "aaaaaaa", "1111111".
   *   3. Reject if it has zero letters and zero digits (pure punctuation).
   *   4. Reject if length ≥ 8 with no vowel AND no digit (likely random
   *      keyboard mashing — but allow short codes like "WD40", "GFX").
   */
  _isLikelyJunkQuery(q) {
    if (typeof q !== 'string') return true;
    var s = q.trim();
    if (s.length < 2) return true;
    if (s.length > 40) return true;

    var lower = s.toLowerCase();

    // Rule 2: dominated by a single repeated character
    var counts = {};
    for (var i = 0; i < lower.length; i++) {
      var ch = lower.charAt(i);
      counts[ch] = (counts[ch] || 0) + 1;
    }
    var topCount = 0;
    for (var k in counts) {
      if (counts[k] > topCount) topCount = counts[k];
    }
    if (s.length >= 5 && topCount / s.length >= 0.7) return true;

    // Rule 3: no letters and no digits at all (Latin or Arabic).
    if (!/[a-z0-9؀-ۿ]/i.test(lower)) return true;

    // Rule 4: long string, no vowel, no digit (random ASCII consonant mash).
    // Skip Arabic-range chars from this check (Arabic doesn't use Latin vowels).
    var hasArabic = /[؀-ۿ]/.test(lower);
    if (!hasArabic && lower.length >= 8 && !/[aeiouy0-9]/.test(lower)) return true;

    return false;
  }

  loadRecentSearches() {
    try {
      var stored = localStorage.getItem('mh_recent_searches');
      var parsed = stored ? JSON.parse(stored) : [];
      if (!Array.isArray(parsed)) return [];
      // Sweep existing junk out of localStorage on load — cleans up old
      // entries that were saved before this filter was added.
      var self = this;
      var clean = parsed.filter(function(s) { return !self._isLikelyJunkQuery(s); });
      if (clean.length !== parsed.length) {
        try { localStorage.setItem('mh_recent_searches', JSON.stringify(clean)); } catch (e) {}
      }
      return clean;
    } catch (e) {
      return [];
    }
  }

  saveRecentSearch(query) {
    if (this._isLikelyJunkQuery(query)) return;

    this.recentSearches = this.recentSearches.filter(function(s) { return s !== query; });
    this.recentSearches.unshift(query);
    this.recentSearches = this.recentSearches.slice(0, 3);

    try {
      localStorage.setItem('mh_recent_searches', JSON.stringify(this.recentSearches));
    } catch (e) {}
  }

  clearRecentSearches() {
    this.recentSearches = [];
    try { localStorage.removeItem('mh_recent_searches'); } catch (e) {}
    this.renderRecentSearches();
  }

  renderRecentSearches() {
    if (!this.recentSearchesList) return;

    if (this.recentSearches.length === 0) {
      if (this.recentSearchesSection) this.recentSearchesSection.style.display = 'none';
      return;
    }

    if (this.recentSearchesSection) this.recentSearchesSection.style.display = 'block';

    var self = this;
    this.recentSearchesList.innerHTML = this.recentSearches.map(function(query) {
      return '<li class="recent-search-item" data-query="' + self.escapeHtml(query) + '">'
        + '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 1v6l4 2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/></svg>'
        + '<span>' + self.escapeHtml(query) + '</span>'
        + '</li>';
    }).join('');
  }

  // Trending Searches
  fetchTrendingSearches() {
    // Only fetch once per page load, then cache
    if (this.trendingTerms !== null) {
      this.renderTrendingSearches();
      this.renderTrendingDropdown();
      return;
    }

    var self = this;
    fetch('https://admin.menahub.com/api/trending_searches.php?limit=20&days=7')
      .then(function(r) { return r.json(); })
      .then(function(data) {
        self.trendingTerms = (data.success && data.terms) ? data.terms : [];
        self.renderTrendingSearches();
        self.renderTrendingDropdown();
      })
      .catch(function() {
        self.trendingTerms = [];
      });
  }

  renderTrendingSearches() {
    if (!this.trendingTagsContainer || !this.trendingSearchesSection) return;

    if (!this.trendingTerms || this.trendingTerms.length === 0) {
      this.trendingSearchesSection.style.display = 'none';
      return;
    }

    this.trendingSearchesSection.style.display = 'block';

    var self = this;
    var trendSvg = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>';

    // Build tags using DOM methods to avoid XSS
    this.trendingTagsContainer.textContent = '';
    this.trendingTerms.forEach(function(term) {
      var btn = document.createElement('button');
      btn.className = 'trending-tag';
      btn.setAttribute('data-trending-query', term);

      var iconSpan = document.createElement('span');
      iconSpan.className = 'trending-tag__icon';
      iconSpan.innerHTML = trendSvg;

      var textSpan = document.createElement('span');
      textSpan.textContent = term;

      btn.appendChild(iconSpan);
      btn.appendChild(textSpan);

      btn.addEventListener('click', function() {
        if (self.input) {
          self.input.value = term;
          self.clearBtn && self.clearBtn.classList.add('visible');
          self.performSearch(term);
        }
      });

      self.trendingTagsContainer.appendChild(btn);
    });
  }

  setupMobileGestures() {
    // Disabled swipe-to-close — it was closing the overlay while users scrolled search results.
    // Users can close via the Cancel button or backdrop tap instead.
  }

  formatMoney(priceString) {
    var amount = parseFloat(priceString);
    if (isNaN(amount)) return priceString;

    var formatted = amount.toFixed(2);
    var noDecimals = Math.floor(amount).toString();
    var withComma = formatted.replace('.', ',');

    var result = this.moneyFormat;
    result = result.replace(/\{\{\s*amount\s*\}\}/g, formatted);
    result = result.replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals);
    result = result.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma);
    result = result.replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals);

    return result;
  }

  escapeHtml(text) {
    var div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Log search queries to MenaHub backend for analytics.
   * Fire-and-forget — never blocks the UI or throws errors.
   */
  _logSearchToBackend(query, event) {
    if (!query || query.length < 2) return;
    try {
      fetch('https://admin.menahub.com/api/search_logs.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: query,
          type: 'website_search',
          event: event || 'typing'
        }),
        keepalive: true
      }).catch(function() {});
    } catch (e) {}
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() {
    window.enhancedSearch = new EnhancedSearchOverlay();
  });
} else {
  window.enhancedSearch = new EnhancedSearchOverlay();
}
